import {
  Box,
  Card,
  FormDialog,
  MuiBox,
  MuiTable,
  SvgIcon,
  SvgIconButton,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { actions as hardwareActions, selectors as hardwareSelectors, Hardware } from 'store/hardware';
import { getSortedList, getUniqueName } from 'utils/stringFormat';
import { HardwareForm, HardwarePayload } from './HardwareForm';
import { KioskHardwareOverlayProp, Mode } from 'views/KiosksView/types';
import { useKioskFormStyles } from '../styled';

export const KioskHardwareOverlay: React.FC<KioskHardwareOverlayProp> = ({ onClose }) => {
  const [selectedHardware, setSelectedHardware] = useState<Hardware | null>(null);
  const [showCreateEdit, setShowCreateEdit] = useState(false);
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false);
  const [actionType, setActionType] = useState<Mode | null>(null);
  const [sortColumn, setSortColumn] = useState<'name' | 'type'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [uiLoading, setUILoading] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const [getHardware, createHardware, updateHardware, deleteHardware, resetError] = useActions([
    hardwareActions.getHardware,
    hardwareActions.createHardware,
    hardwareActions.updateHardware,
    hardwareActions.deleteHardware,
    hardwareActions.resetError
  ]);

  const hardware = useSelector(hardwareSelectors.selectHardware);
  const createdHardware = useSelector(hardwareSelectors.selectCreatedHardware);
  const { error, pending } = useSelector(hardwareSelectors.selectAction);

  const sortedHardware = useMemo(() => {
    if (!hardware?.length) return [];

    const sorted = getSortedList(hardware, sortColumn);
    return sortOrder === 'asc' ? sorted : [...sorted].reverse();
  }, [hardware, sortColumn, sortOrder]);

  const classes = useKioskFormStyles();

  useEffect(() => {
    getHardware();
  }, [getHardware]);

  useEffect(() => {
    if (uiLoading && !pending) {
      if (!error) {
        setShowCreateEdit(false);
        setSelectedHardware(null);
        setActionType(null);
        getHardware();
        // A finished copy opens the new hardware for editing
        if (isCopying && createdHardware) {
          setSelectedHardware(createdHardware);
          setActionType('edit');
          setShowCreateEdit(true);
        }
      }
      setIsCopying(false);
      setUILoading(false);
    }
  }, [uiLoading, pending, error, getHardware, isCopying, createdHardware]);

  const handleSort = (column: 'name' | 'type') => {
    if (sortColumn === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  // The form stays open until the save finishes, so an error keeps what was typed
  const handleSubmit = (payload: HardwarePayload) => {
    setUILoading(true);

    if (payload.id) {
      updateHardware(payload);
    } else {
      createHardware(payload);
    }
  };

  const handleCopy = (item: Hardware) => {
    setUILoading(true);
    setIsCopying(true);
    createHardware({
      name: getUniqueName(item.name, hardware),
      code: item.code,
      type: item.type,
      details: item.details
    });
  };

  const handleDelete = () => {
    if (!selectedHardware) return;

    setUILoading(true);
    deleteHardware({ id: selectedHardware.id });

    setSelectedHardware(null);
    setShowDeleteOverlay(false);
  };

  const SortArrow = ({ active }: { active: boolean }) =>
    active ? (
      <SvgIcon name={sortOrder === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
    ) : null;

  return (
    <FormDialog
      open
      title="Hardware"
      confirmButtonAction={onClose}
      confirmButtonText="Close"
      headerButtonAction={() => {
        setShowCreateEdit(true);
        setActionType('create');
      }}
      headerButtonText="Create Hardware"
    >
      <MuiBox mb="1rem" mt="14px">
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <MuiTable.TableContainer>
              <MuiTable.Table stickyHeader>
                <MuiTable.TableHead>
                  <MuiTable.TableRow>
                    <MuiTable.TableCell
                      className={classes.kioskDetailsHeaders}
                      sx={{ width: '220px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                      onClick={() => handleSort('name')}
                    >
                      Name <SortArrow active={sortColumn === 'name'} />
                    </MuiTable.TableCell>
                    <MuiTable.TableCell
                      className={classes.kioskDetailsHeaders}
                      sx={{ width: '220px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                      onClick={() => handleSort('type')}
                    >
                      Type <SortArrow active={sortColumn === 'type'} />
                    </MuiTable.TableCell>
                    <MuiTable.TableCell
                      className={classes.kioskDetailsHeaders}
                      sx={{ width: '220px', whiteSpace: 'nowrap' }}
                    >
                      Code
                    </MuiTable.TableCell>
                    <MuiTable.TableCell
                      className={classes.kioskDetailsHeaders}
                      sx={{ cursor: 'default', textAlign: 'center' }}
                    >
                      Actions
                    </MuiTable.TableCell>
                  </MuiTable.TableRow>
                </MuiTable.TableHead>

                <MuiTable.TableBody>
                  {sortedHardware.map((item: Hardware) => (
                    <MuiTable.TableRow key={item.id}>
                      <MuiTable.TableCell>{item.name}</MuiTable.TableCell>
                      <MuiTable.TableCell>{item.type}</MuiTable.TableCell>
                      <MuiTable.TableCell>{item.code}</MuiTable.TableCell>

                      <MuiTable.TableCell align="center">
                        <MuiBox display="flex" gap="10px" justifyContent="center">
                          <Tooltip title="Edit Hardware" arrow placement="top" verticalOffset={-8}>
                            <SvgIconButton
                              data-testid="edit-hardware-button"
                              onClick={() => {
                                setShowCreateEdit(true);
                                setSelectedHardware(item);
                                setActionType('edit');
                              }}
                            >
                              <SvgIcon name="edit_circle" />
                            </SvgIconButton>
                          </Tooltip>

                          <Tooltip title="Copy Hardware" arrow placement="top" verticalOffset={-8}>
                            <SvgIconButton
                              onClick={() => handleCopy(item)}
                              disabled={uiLoading || pending}
                              data-testid="copy-hardware-button"
                            >
                              <SvgIcon name="copy_circle" />
                            </SvgIconButton>
                          </Tooltip>

                          <Tooltip title="Delete Hardware" arrow placement="top" verticalOffset={-8}>
                            <SvgIconButton
                              onClick={() => {
                                setSelectedHardware(item);
                                setShowDeleteOverlay(true);
                              }}
                            >
                              <SvgIcon name="exit_circle" />
                            </SvgIconButton>
                          </Tooltip>
                        </MuiBox>
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  ))}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Box>
        </Card>
        {showDeleteOverlay && (
          <FormDialog
            open={showDeleteOverlay}
            confirmButtonAction={handleDelete}
            confirmButtonText="Delete"
            cancelButtonAction={() => {
              setSelectedHardware(null);
              setShowDeleteOverlay(false);
            }}
            cancelButtonText="Cancel"
          >
            <MuiBox>
              <Typography fontSize={24}>Are you sure you want to delete this hardware?</Typography>
            </MuiBox>
          </FormDialog>
        )}

        {showCreateEdit && actionType && (
          <HardwareForm
            mode={actionType}
            initialData={selectedHardware ? selectedHardware : undefined}
            onClose={() => {
              setShowCreateEdit(false);
              setSelectedHardware(null);
              setActionType(null);
              getHardware();
            }}
            onSubmit={handleSubmit}
          />
        )}

        {error && (
          <FormDialog cancelButtonAction={resetError} confirmButtonAction={resetError} confirmButtonText="Ok" open>
            <Typography fontSize={24} maxWidth="524px">
              {error}
            </Typography>
          </FormDialog>
        )}
      </MuiBox>
    </FormDialog>
  );
};
